/**
 * Sanitizes TypeORM where clause objects by detecting and removing
 * potentially dangerous SQL injection patterns.
 *
 * This provides defense-in-depth even though TypeORM parameterizes queries.
 */

// Dangerous SQL keywords and patterns that shouldn't appear in object keys or values
const DANGEROUS_SQL_PATTERNS = [
  // SQL commands that should never appear in where clauses
  "DROP TABLE",
  "DROP DATABASE",
  "DELETE FROM",
  "TRUNCATE",
  "ALTER TABLE",
  "CREATE TABLE",
  "INSERT INTO",
  "UPDATE SET",
  "EXEC ",
  "EXECUTE ",
  "UNION SELECT",
  "UNION ALL",
  "DECLARE @",
  // SQL injection comment patterns (with context to avoid false positives)
  " --",
  ";--",
  "/*",
  "*/",
  // Stored procedure patterns
  "xp_cmdshell",
  "sp_executesql",
  // Common SQL injection techniques
  "OR 1=1",
  "OR '1'='1",
  "' OR '",
  '" OR "',
  "AND 1=1",
  "; DROP",
  "'; DROP",
  "1' OR '1",
];

/**
 * Checks if an object is a TypeORM FindOperator
 * Uses the same check that TypeORM itself uses internally
 */
function isTypeORMFindOperator(obj: unknown): boolean {
  if (typeof obj !== "object" || obj === null) {
    return false;
  }

  // TypeORM uses a Symbol to mark FindOperators
  // See: typeorm/util/InstanceChecker.ts
  const instanceofSymbol = (obj as Record<string, unknown>)["@instanceof"];
  return (
    instanceofSymbol === Symbol.for("FindOperator") ||
    instanceofSymbol === Symbol.for("EqualOperator")
  );
}

/**
 * Checks if a string contains dangerous SQL patterns
 */
function containsDangerousPattern(str: string): boolean {
  const upperStr = str.toUpperCase();
  return DANGEROUS_SQL_PATTERNS.some((pattern) =>
    upperStr.includes(pattern.toUpperCase()),
  );
}

/**
 * Recursively removes dangerous SQL patterns from an object
 */
function sanitizeObject(
  obj: unknown,
  foundPatterns: Array<{ key: string; value: unknown }> = [],
): unknown {
  if (obj === null || obj === undefined) {
    return obj;
  }

  if (Array.isArray(obj)) {
    return obj.map((item) => sanitizeObject(item, foundPatterns));
  }

  if (typeof obj === "object") {
    // Don't sanitize TypeORM FindOperators - they need to be preserved
    if (isTypeORMFindOperator(obj)) {
      return obj;
    }

    const sanitized: { [key: string]: unknown } = {};

    for (const [key, value] of Object.entries(obj)) {
      // Check if key contains dangerous SQL patterns
      if (containsDangerousPattern(key)) {
        foundPatterns.push({ key, value });
      } else {
        // Check if string values contain dangerous patterns
        if (typeof value === "string" && containsDangerousPattern(value)) {
          foundPatterns.push({ key, value });
        } else {
          // Recursively sanitize nested objects
          sanitized[key] = sanitizeObject(value, foundPatterns);
        }
      }
    }

    return sanitized;
  }

  return obj;
}

/**
 * Sanitizes a TypeORM where clause by removing dangerous SQL patterns
 *
 * @param whereClause - The where clause object or array to sanitize
 * @param logger - Optional callback when dangerous patterns are found, console.warn used if none provided.
 * @returns Sanitized where clause
 */
export function baseSanitizeWhereClause<T>(
  whereClause: T,
  logger?: (msg: string) => void,
): T {
  const foundPatterns: Array<{ key: string; value: unknown }> = [];
  const sanitized = sanitizeObject(whereClause, foundPatterns) as T;

  if (foundPatterns.length > 0) {
    // NOTE: matching keys are DROPPED from the returned where clause. That can
    // change query results (a removed condition makes the query broader), so
    // this is logged explicitly rather than failing silently. If a legitimate
    // value trips a pattern, that is a false positive to investigate — the real
    // SQL-injection protection is TypeORM's parameterization, not this blocklist.
    const msg = `sanitizeWhereClause removed ${foundPatterns.length} where-clause field(s) matching a blocklisted SQL pattern; the affected condition(s) were dropped from the query. Review whether this was a genuine injection attempt or a false positive. Dropped: ${foundPatterns.map((p) => JSON.stringify(p)).join(", ")}`;

    logger ? logger(msg) : console.warn(msg);
  }

  return sanitized;
}
